import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { t } from 'i18next';
import { format } from 'date-fns';
import SecondaryButton from '../../lib/ui/buttons/SecondaryButton';
import AdditionalServices from './AdditionalServices';
import {
  Body,
  Content,
  DeliveryStatus,
  Header,
  PageContainer,
  PageTitleContainer,
  ProgressBar,
  ProgressBarTrack,
  ProgressContainer,
  ProgressStatus,
  ShipmentCreatedAt,
  ShipmentHeading,
  ShipmentOptionsButtonLabel,
  ShipmentOptionsContainer,
  StatusContainer,
  Wrapper,
  Table,
  TableCell,
  CellLabel,
  CellValueLink,
  TableDivider,
} from './css';
import {
  DeliveryPhotoSection,
  DeliveryPhotoTitle,
  DeliveryPhotoInputRow,
  DeliveryPhotoPostalInputWrapper,
  DeliveryPhotoPostalInput,
  DeliveryPhotoError,
} from '../../lib/ui/table/css';
import Measurements from './Measurements';
import PickupDetails from './PickupDetails';
import PODDocuments from './PODDocuments';
import ServiceAndPricing from './ServiceAndPricing';
import ShipmentInfo from './ShipmentInfo';
import ShipmentSummary from './ShipmentSummary';
import Tracking from '../../lib/ui/maps/Tracking/index';
import { getOrder, uploadFiles } from './api';
import downloadDocument from './PODDocuments/api';
import ActionsMenu from './ActionsMenu';
import Breadcrumbs from '../../lib/ui/breadcrumbs';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import QuestionsBlock from '../ReactComponent/QuestionsBlock';

class ViewOrder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMenuOpen: false,
      probillNumber: '',
      postalCode: '',
      postalError: '',
      safeDropPhotos: null,
    };
  }

  componentDidMount() {
    const { accountNumbers } = this.props;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      this.setState({
        probillNumber: urlParams.get('probillNumber'),
      }, () => {
        getOrder(this.state.probillNumber, this.updateState, accountNumbers);
      });
    }
  }

  filterTrackingAfterDelivered = (trackingData) => {
    if (!trackingData || trackingData.length === 0) {
      return trackingData;
    }

    // Find which tracking event contains "Delivered" and at which touchpoint
    let deliveredEventIndex = -1;
    let deliveredTouchPointIndex = -1;
    let latestDeliveredTime = null;

    for (let i = 0; i < trackingData.length; i++) {
      const event = trackingData[i];
      if (event.touchPoints && event.touchPoints.length > 0) {
        for (let j = 0; j < event.touchPoints.length; j++) {
          const touchPoint = event.touchPoints[j];
          const status = touchPoint.status?.toLowerCase() || '';

          if (status.includes('delivered') || status.includes('livré') || status.includes('order canceled') || status.includes('commande annulée')) {
            const currentTime = touchPoint.time;

            if (deliveredEventIndex === -1
              || (currentTime && (!latestDeliveredTime || new Date(currentTime) >= new Date(latestDeliveredTime)))) {
              deliveredEventIndex = i;
              deliveredTouchPointIndex = j;
              latestDeliveredTime = currentTime;
            }
          }
        }
      }
    }

    // If no "Delivered" found, return original data
    if (deliveredEventIndex === -1) {
      return trackingData;
    }

    // Keep only tracking events up to and including the one with "Delivered"
    const filteredTracking = trackingData.slice(0, deliveredEventIndex + 1);

    // In the last tracking event, keep only touchpoints up to and including "Delivered"
    if (filteredTracking.length > 0) {
      const lastEvent = filteredTracking[filteredTracking.length - 1];
      lastEvent.touchPoints = lastEvent.touchPoints.slice(0, deliveredTouchPointIndex + 1);
    }

    return filteredTracking;
  };

  handleSubmitFiles = (formData) => {
    if (typeof window !== 'undefined') {
      const files = Object.entries(formData.attachedDocuments);

      const filesToUpload = files.map((file) => {
        const category = file[0];
        const [fileInfo] = Object.entries(file[1]);
        const fileName = fileInfo[1].name;
        const fileDataAsBase64 = fileInfo[1].file;
        const fileData = { category, fileName, fileDataAsBase64 };
        return fileData;
      });

      const { accountNumbers } = this.props;
      const urlParams = new URLSearchParams(window.location.search);
      this.setState({
        probillNumber: urlParams.get('probillNumber'),
      }, () => {
        uploadFiles(this.state.probillNumber, this.updateState, accountNumbers, filesToUpload);
      });
    }
  };

  updateState = (name, value) => this.setState({ [name]: value });

  normalisePostal = (value) => (value || '').replace(/\s+/g, '').toUpperCase();

  handlePostalChange = (e) => {
    const { value } = e.target;
    this.setState({ postalCode: value, safeDropPhotos: null });

    if (!value) {
      this.setState({ postalError: '' });
      return;
    }

    const consigneePostal = this.state.data && this.state.data.to && this.state.data.to.postalCode;
    if (this.normalisePostal(value) !== this.normalisePostal(consigneePostal)) {
      this.setState({ postalError: t('PostalCodeError') });
    } else {
      this.setState({ postalError: '' });
    }
  };

  handleViewPhoto = () => {
    const docs = (this.state.data && this.state.data.documents) || [];
    const photos = docs.filter((doc) => doc.fileType === 'Safe_Drop_Photo');
    this.setState({ safeDropPhotos: photos });
  };

  render() {
    const { isAuthenticated } = this.props;
    if (this.state.data) {
      const {
        tracking,
        shipmentNumber,
        createDate,
        status,
        statusCode,
        statusPercentage,
        statusColor,
        estimatedDeliveryDate,
        probillNumber,
        poNumber,
        pallets,
        pieces,
        weight,
        from,
        to,
        billToAccountNumber,
        brokerName,
        brokerEmail,
        brokerPhoneNumber,
        items,
        droppingFreightOff,
        hasRegularPickups,
        readyTime,
        closingTime,
        notes,
        accessorials,
        serviceLevelDescription,
        totalCharges,
        tax,
        charges,
        totalAmount,
        documents,
        referenceNumber,
        rlProbill,
        signedBy,
        emergencyPhoneNumber,
        erapReferenceNumber,
        erapPhoneNumber,
        orderId,
        deliveryDate,
        deliveryAppointment,
        deliverByEnd,
      } = this.state.data;

      const isExceptionStatus = (st, sc) => (/^Exception - Call Customer Service$/i.test(st)
        || /^Exception - Appeler le service à la clientèle/i.test(st)
        || /^Exception - OSD/i.test(st)
        || /^Livré - OSD/i.test(st))
        && sc === 'COMPLETE';

      const exceptionStatus = isExceptionStatus(status, statusCode)
        || (tracking && tracking.some((event) =>
          (event.touchPoints || []).some((tp) => isExceptionStatus(tp.status, tp.statusCode))));

      // Filter tracking data to remove statuses after "Delivered"
      const filteredTracking = this.filterTrackingAfterDelivered(tracking);

      const shipmentInfo = {
        probillNumber,
        orderId,
        poNumber,
        referenceNumber,
        rlProbill,
        signedBy,
        pallets,
        pieces,
        totalWeight: `${weight.value} ${weight.unitOfMeasure}`,
        deliveryAppointment,
        deliverByEnd,
      };

      const shipmentSummary = {
        shipperDetails: from,
        consigneeDetails: to,
        billToAccountNumber,
        brokerName,
        brokerEmail,
        brokerPhoneNumber,
      };

      const measurements = {
        items,
        pallets,
        pieces,
        totalWeight: `${weight.value}${weight.unitOfMeasure}`,
        referenceNumber,
        poNumber,
        emergencyPhoneNumber,
        erapReferenceNumber,
        erapPhoneNumber,
      };

      const pickupDetails = {
        pickupDate: readyTime,
        readyTime,
        closingTime,
        notes,
        droppingFreightOff,
        hasRegularPickups,
      };

      const serviceAndPricing = {
        serviceLevel: serviceLevelDescription,
        chargesPrice: totalCharges,
        taxPrice: tax,
        invoiceItems: charges,
        totalPrice: totalAmount,
        estimatedDeliveryDate,
      };
      const additionalServices = accessorials.map(({ description }) => description);

      return (
        <Wrapper>
          <div className="container">
            <Breadcrumbs
              nopadding="no-padding-left"
              isAuthenticated={isAuthenticated}
              items={[t('ViewOrderBreadcrumbTitle'), `${t('Shipment')} ${shipmentNumber}`]}
            />
            <PageContainer>
              <Header>
                <PageTitleContainer>
                  <ShipmentHeading>
                    {t('Shipment')}
                    :
                    {' '}
                    {shipmentNumber}
                  </ShipmentHeading>
                  <ShipmentCreatedAt>
                    {t('CreatedIn')}
                    {' '}
                    {format(createDate, t('DATE_FORMAT_PATTERN'))}
                  </ShipmentCreatedAt>
                </PageTitleContainer>
                <StatusContainer>

                  <ProgressContainer>
                    <ProgressBar>
                      <ProgressBarTrack progress={statusPercentage} color={statusColor} />
                    </ProgressBar>
                    <ProgressStatus>{t(status)}</ProgressStatus>
                  </ProgressContainer>

                  <DeliveryStatus>
                    {exceptionStatus ? (
                      <>
                        {t('EstDelivery')}
                        :
                      </>
                    ) : (deliveryDate ? (
                      <>
                        {t('EstDelivery')}
                        :
                        {' '}
                        <br />
                        <strong>
                          {format(deliveryDate, t('DATE_DELIVERY_PATTERN'))}
                        </strong>
                      </>
                    ) : (
                      <>
                        {t('EstDelivery')}
                        :
                        {' '}
                        <br />
                        <strong>
                          {format(deliveryAppointment || estimatedDeliveryDate, t('DATE_FORMAT_PATTERN'))}
                        </strong>
                      </>
                    ))}
                  </DeliveryStatus>

                  <ShipmentOptionsContainer>
                    <SecondaryButton
                      showArrow={false}
                      size="small"
                      onClick={(prevState) => this.setState({ isMenuOpen: !prevState.isMenuOpen })}
                    >
                      <ShipmentOptionsButtonLabel>
                        {t('Options')}
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M9 2L5 6L1 2"
                            stroke="#FF5F14"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </ShipmentOptionsButtonLabel>
                    </SecondaryButton>
                    <ActionsMenu
                      isMenuOpen={this.state.isMenuOpen}
                      closeMenu={() => this.setState({ isMenuOpen: false })}
                      probillNumber={shipmentNumber}
                      orderId={this.state.probillNumber}
                      postalZipCode={to.postalCode}
                      status={this.state.data.status}
                      isCAShipper={from.countryCode === 'CA'}
                      pickupDone={this.state.data.pickupDone}
                      shipmentNumber={shipmentNumber}
                    />

                  </ShipmentOptionsContainer>
                </StatusContainer>
              </Header>

              <Body>
                <ShipmentInfo data={shipmentInfo} />
                <Content>
                  <PODDocuments data={documents} handleSubmitFiles={this.handleSubmitFiles} accountNumbers={this.props.accountNumbers} />
                  <ShipmentSummary data={shipmentSummary} />
                  <Measurements data={measurements} />
                  <Tracking data={filteredTracking} />
                  <DeliveryPhotoSection>
                    <DeliveryPhotoTitle>{t('CheckForDeliveryPhoto')}</DeliveryPhotoTitle>
                    <DeliveryPhotoInputRow>
                      <DeliveryPhotoPostalInputWrapper>
                        <DeliveryPhotoPostalInput
                          type="text"
                          placeholder={t('ConsigneePostalCode')}
                          value={this.state.postalCode}
                          onChange={this.handlePostalChange}
                          hasError={!!this.state.postalError}
                          aria-label={t('ConsigneePostalCode')}
                        />
                        {this.state.postalError && (
                          <DeliveryPhotoError>{this.state.postalError}</DeliveryPhotoError>
                        )}
                      </DeliveryPhotoPostalInputWrapper>
                      <PrimaryButton
                        type="button"
                        disabled={
                          !this.state.postalCode
                          || this.normalisePostal(this.state.postalCode) !== this.normalisePostal(to && to.postalCode)
                        }
                        onClick={this.handleViewPhoto}
                      >
                        {t('ViewPhoto')}
                      </PrimaryButton>
                    </DeliveryPhotoInputRow>

                    {this.state.safeDropPhotos && this.state.safeDropPhotos.length > 0 && (
                      <Table>
                        {this.state.safeDropPhotos.map(({ documentId, fileType }) => (
                          <React.Fragment key={documentId}>
                            <TableCell>
                              <CellLabel>{fileType}</CellLabel>
                              <CellValueLink
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadDocument(documentId, null, documentId);
                                }}
                              >
                                {t('View')}
                              </CellValueLink>
                            </TableCell>
                            <TableDivider />
                          </React.Fragment>
                        ))}
                      </Table>
                    )}

                    {this.state.safeDropPhotos && this.state.safeDropPhotos.length === 0 && (
                      <DeliveryPhotoError>{t('NoPhotoAvailable')}</DeliveryPhotoError>
                    )}
                  </DeliveryPhotoSection>
                  <ServiceAndPricing data={serviceAndPricing} />
                  <PickupDetails data={pickupDetails} />
                  <AdditionalServices data={additionalServices} />
                </Content>
              </Body>

            </PageContainer>
            <QuestionsBlock />
          </div>
        </Wrapper>
      );
    }

    return <div />;
  }
}

ViewOrder.propTypes = {
  isAuthenticated: PropTypes.bool,
  accountNumbers: PropTypes.array,
};

const mstp = (state) => ({
  serviceLevelQuotes: state.user.serviceLevelQuotes,
  isAuthenticated: state.profile.isAuthenticated,
  accountNumbers: state.profile.accountNumbers,
  division: state.profile.division,
  userId: state.profile.userId,
  userEmail: state.profile.email,
  billingAccounts: state.profile.billingAccounts,
});

export default connect(mstp)(ViewOrder);
